import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { resolveChannelIcon } from '@/components/bots/Integration/ChannelLogo';

import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The agent's Gmail channel once connected: the mailbox, and the choice the
 * owner makes for every new email, drafts first.
 */
export default function MailboxSettings({ address }: { address: string }) {
  const { t } = useChannelsT();

  const modes = [
    {
      title: t('Save a draft reply'),
      badge: t('Recommended'),
      detail: t(
        'The agent writes the reply as a draft in the same thread. Nothing is sent without you.',
      ),
      active: true,
    },
    {
      title: t('Send the reply automatically'),
      detail: t(
        'The agent answers right away from this mailbox. Best once its drafts look right.',
      ),
      active: false,
    },
  ];

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-16'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] p-5 text-start shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center gap-3'>
          <span className='flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
            {resolveChannelIcon('Gmail', 'h-5 w-5')}
          </span>
          <span className='min-w-0 flex-1'>
            <span
              className={clsxm('block text-[15px] font-semibold', tone.ink)}
            >
              Gmail
            </span>
            <span className={clsxm('block truncate text-xs', tone.quiet)}>
              <span dir='ltr'>{address}</span>
            </span>
          </span>
          <span className='flex flex-none items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
            <CheckGlyph className='h-3.5 w-3.5' />
            {t('Connected')}
          </span>
        </div>

        <p className={clsxm('mt-5 text-[13px] font-semibold', tone.ink)}>
          {t('When a new email arrives')}
        </p>
        <div className='mt-2.5 space-y-2'>
          {modes.map(({ title, badge, detail, active }) => (
            <div
              key={title}
              className={clsxm(
                'flex gap-3 rounded-xl p-3.5 ring-1 ring-inset',
                active
                  ? 'bg-primary-50/60 ring-primary-600/40 dark:bg-primary-400/10 dark:ring-primary-400/40'
                  : 'ring-[#0f0f28]/10 dark:ring-white/10',
              )}
            >
              <span
                className={clsxm(
                  'mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full ring-1 ring-inset',
                  active
                    ? 'bg-primary-600 ring-primary-600'
                    : 'ring-[#0f0f28]/25 dark:ring-white/30',
                )}
              >
                {active && (
                  <span className='h-1.5 w-1.5 rounded-full bg-white' />
                )}
              </span>
              <span className='min-w-0'>
                <span
                  className={clsxm(
                    'flex flex-wrap items-center gap-2 text-[13.5px] font-semibold',
                    tone.ink,
                  )}
                >
                  {title}
                  {badge ? (
                    <span className='rounded-full bg-primary-600 px-2 py-0.5 text-[10.5px] font-semibold text-white'>
                      {badge}
                    </span>
                  ) : null}
                </span>
                <span
                  className={clsxm('mt-1 block text-xs leading-5', tone.body)}
                >
                  {detail}
                </span>
              </span>
            </div>
          ))}
        </div>
        <p className={clsxm('mt-4 text-xs', tone.quiet)}>
          {t('Only email that arrives after you connect is answered.')}
        </p>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-center gap-3 rounded-2xl p-3.5 pe-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-3 sm:end-[-1.5rem] sm:mt-0 sm:w-[18rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
          {resolveChannelIcon('Outlook', 'h-5 w-5')}
        </span>
        <span className='min-w-0 flex-1'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            Outlook
          </span>
          <span className={clsxm('block text-xs leading-snug', tone.quiet)}>
            {t('Outlook and Microsoft 365')}
          </span>
        </span>
        <span className='flex-none rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-white'>
          {t('Connect')}
        </span>
      </div>
    </div>
  );
}
